import {
  WSActions,
  WSCard,
  WSControlGroup,
  WSDivider,
  WSForm,
  WSInfoBox,
  WSInputNumber,
  WSList,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  TaxEstimateFilingStatus,
  TaxWithholdingSettings,
  UsTaxState
} from "@wingspanhq/wingspan-api-sdk";
import { useCallback } from "react";
import * as Yup from "yup";
import { ErrorAlert } from "@components/ErrorAlert";
import {
  QUERY_V3_TAX_ESTIMATE,
  useQueryTaxEstimate
} from "@hooks/useQueryTaxEstimate";
import { queriesTaxWithholdings } from "@query";
import { WSQueries } from "../../../query/WSQuery";
import { selectorTaxRateLabel } from "../../../shared/selectors/selectorTaxRateLabel";
import { FILING_STATUS_OPTIONS, US_TAX_STATE_OPTIONS } from "../constants";
import { useModalHowItWorks } from "../modals/ModalHowItWorks";
import { useModalHowWeCalculateTaxRate } from "../modals/ModalHowWeCalculateTaxRate";
import { selectorIncomeOptionsByFilingStatus } from "../selectors/selectorIncomeOptionsByFilingStatus";

const MIN_WITHHOLDING_RATE = 1;
const MAX_WITHHOLDING_RATE = 100;

const isUsableRate = (rate?: number | null): rate is number =>
  rate !== undefined &&
  rate !== null &&
  rate >= MIN_WITHHOLDING_RATE &&
  rate <= MAX_WITHHOLDING_RATE;

const UNUSABLE_ESTIMATE_ERROR = new Error(
  "Tax estimate returned no usable effective rate"
);

export type RateSummaryProps = {
  rate?: number;
  hasAnswers: boolean;
  isCalculated: boolean;
  isFetching: boolean;
  error: unknown;
};

export const RateSummary: React.FC<RateSummaryProps> = props => {
  if (isUsableRate(props.rate)) {
    return (
      <WSInfoBox>
        Your recommended withholding rate: {selectorTaxRateLabel(props.rate)} of
        gross payments
      </WSInfoBox>
    );
  }

  if (props.isFetching) {
    return (
      <WSInfoBox>Calculating your recommended withholding rate…</WSInfoBox>
    );
  }

  if (!props.hasAnswers) {
    return (
      <WSInfoBox>
        Answer the questions above to see your recommended withholding rate.
      </WSInfoBox>
    );
  }

  // Checked against the error so a failed calculation isn't reported as one the
  // member has yet to ask for.
  if (!props.error && !props.isCalculated) {
    return (
      <WSInfoBox>
        Calculate your rate to see our recommendation for these answers.
      </WSInfoBox>
    );
  }

  return (
    <ErrorAlert
      context="TaxEstimate"
      error={props.error ?? UNUSABLE_ESTIMATE_ERROR}
    />
  );
};

/** `estimatedAnnualIncome` is a band's figure as a string — select options are strings. */
type FormData = {
  filingStatus: TaxEstimateFilingStatus;
  stateOfResidence: UsTaxState;
  estimatedAnnualIncome: string;
  preference: "estimated" | "custom";
  customRate?: number | null;
};

type TaxProfileAnswers = Pick<
  FormData,
  "filingStatus" | "stateOfResidence" | "estimatedAnnualIncome"
>;

const toTaxProfileUpdate = (answers: TaxProfileAnswers) => ({
  filingStatus: answers.filingStatus,
  state: answers.stateOfResidence,
  estimatedAnnualIncome: Number(answers.estimatedAnnualIncome)
});

type Props = {
  onBack?: () => void;
  onNext?: () => void;
  onSkip?: () => void;
  isEdit?: boolean;
};

/**
 * Tax profile and withholding rate on one screen.
 *
 * v1 split these across two screens and used the server as the handoff. v3 keeps
 * one screen but the same two writes: `PATCH /v3/finance/tax-withholdings/settings`
 * stores the profile, `POST /v3/finance/tax-estimates` computes the rate from
 * what it stored, and the accepted rate is written back to the same settings.
 * The estimate takes `{ year }` alone, so the answers reach it only once saved —
 * which is why calculating is a button rather than something the form does as
 * the member is still picking.
 */
const TaxRateForm: React.FC<Props & { settings: TaxWithholdingSettings }> =
  props => {
    const { settings, onNext } = props;
    const year = new Date().getFullYear();

    const modalHowItWorks = useModalHowItWorks();
    const modalHowWeCalculateTaxRate = useModalHowWeCalculateTaxRate();

    const hasSavedProfile = !!settings.filingStatus && !!settings.state;

    const queryTaxEstimate = useQueryTaxEstimate(year, {
      enabled: hasSavedProfile
    });

    // Whether the estimate on hand was computed from these answers — the saved
    // profile is what the backend calculated with, so comparing against it needs
    // no separate record of what was last submitted.
    const isSavedProfile = useCallback(
      (answers: TaxProfileAnswers) =>
        !!answers.filingStatus &&
        !!answers.stateOfResidence &&
        !!answers.estimatedAnnualIncome &&
        settings.filingStatus === answers.filingStatus &&
        settings.state === answers.stateOfResidence &&
        settings.estimatedAnnualIncome ===
          Number(answers.estimatedAnnualIncome),
      [settings.estimatedAnnualIncome, settings.filingStatus, settings.state]
    );

    const [saveProfile, saveProfileMeta] =
      queriesTaxWithholdings.updateTaxWithholdingSettings.useMutation({
        // Await the settings refetch: the recalculated rate is only readable as
        // the member's once the profile it was computed from is the saved one.
        awaitDependencies: [
          queriesTaxWithholdings.getTaxWithholdingSettings.queryKeyBase
        ],
        dependencies: [[QUERY_V3_TAX_ESTIMATE]]
      });

    const [saveRate, saveRateMeta] =
      queriesTaxWithholdings.updateTaxWithholdingSettings.useMutation({
        // Await the settings refetch before advancing so the next step doesn't
        // briefly render the previous rate.
        awaitDependencies: [
          queriesTaxWithholdings.getTaxWithholdingSettings.queryKeyBase
        ],
        dependencies: [[QUERY_V3_TAX_ESTIMATE]]
      });

    const submit = useCallback(
      async (data: FormData) => {
        const withholdingRate =
          data.preference === "custom"
            ? data.customRate
            : isSavedProfile(data)
              ? queryTaxEstimate.data?.effectiveTaxRate
              : undefined;

        // Unreachable: Continue is disabled without a calculated rate, and the
        // custom rate is bounded by the schema.
        if (!isUsableRate(withholdingRate)) {
          return;
        }

        // Profile and rate in one write, so a failure can't leave the answers
        // saved against a rate the member never accepted.
        await saveRate(
          {
            taxWithholdingSettingsUpdate: {
              ...toTaxProfileUpdate(data),
              withholdingRate
            }
          },
          { onSuccess: onNext }
        );
      },
      [isSavedProfile, onNext, queryTaxEstimate.data?.effectiveTaxRate, saveRate]
    );

    return (
      <WSForm<FormData>
        defaultValues={{
          filingStatus: settings.filingStatus,
          stateOfResidence: settings.state,
          estimatedAnnualIncome: settings.estimatedAnnualIncome
            ? String(settings.estimatedAnnualIncome)
            : undefined,
          // v1 compared the saved rate against the estimate to decide whether the
          // member had picked "calculated" or "custom". v3 stores the number
          // without the preference — so an existing rate preselects Custom holding
          // that value. That keeps a deliberate custom rate intact (never silently
          // replaced); the cost is that a previously-accepted calculated rate reads
          // back as custom until the member reselects the calculated option.
          preference: settings.withholdingRate ? "custom" : "estimated",
          customRate: settings.withholdingRate
        }}
        validationSchema={Yup.object().shape({
          filingStatus: Yup.string().nullable().required("Required"),
          stateOfResidence: Yup.string().nullable().required("Required"),
          estimatedAnnualIncome: Yup.string()
            .nullable()
            .required("Required")
            // The bands on offer change with the filing status, so a value
            // picked under the previous one has to be chosen again.
            .test("income-band", "Required", function (value) {
              return selectorIncomeOptionsByFilingStatus(
                this.parent.filingStatus
              ).some(option => option.value === value);
            }),
          preference: Yup.string().required("Required"),
          customRate: Yup.number().when("preference", {
            is: "custom",
            then: Yup.number()
              .nullable()
              .required("Required")
              .min(MIN_WITHHOLDING_RATE, `Min value is ${MIN_WITHHOLDING_RATE}`)
              .max(MAX_WITHHOLDING_RATE, `Max value is ${MAX_WITHHOLDING_RATE}`)
          })
        })}
        onSubmit={submit}
      >
        <WSList gap="2XL">
          <WSCard
            header={
              props.isEdit
                ? "Your tax profile"
                : "Calculate your estimated tax rate"
            }
          >
            {!props.isEdit && (
              <>
                <WSText.ParagraphSm color="gray500">
                  Let’s determine how much you should set aside.{" "}
                  <a
                    onClick={() => {
                      modalHowWeCalculateTaxRate.open();
                    }}
                  >
                    Learn more
                  </a>{" "}
                  about your rate calculation.
                </WSText.ParagraphSm>

                <WSDivider my="L" />
              </>
            )}

            <WSList gap="2XL">
              <WSForm.Field
                name="filingStatus"
                component={WSSelect}
                componentProps={{
                  label: "What’s your filing status?",
                  required: true,
                  helperText:
                    "We suggest selecting the same filing status as last year unless you’ve had a major life event.",
                  options: FILING_STATUS_OPTIONS
                }}
              />

              <WSForm.Field
                name="stateOfResidence"
                component={WSSelect}
                componentProps={{
                  label: "What’s your State of residence?",
                  required: true,
                  helperText:
                    "We’ll factor both Federal and State estimates to increase the accuracy of our recommendation.",
                  internalSearch: true,
                  options: US_TAX_STATE_OPTIONS
                }}
              />

              <WSForm.Values names={["filingStatus"]}>
                {(values: Pick<FormData, "filingStatus">) => (
                  <WSForm.Field
                    name="estimatedAnnualIncome"
                    component={WSSelect}
                    componentProps={{
                      label: "What’s your estimated annual income?",
                      required: true,
                      helperText:
                        "Your best estimate of what you’ll earn from freelance work this year.",
                      options: selectorIncomeOptionsByFilingStatus(
                        values.filingStatus
                      )
                    }}
                  />
                )}
              </WSForm.Values>
            </WSList>
          </WSCard>

          <WSForm.Values
            names={[
              "filingStatus",
              "stateOfResidence",
              "estimatedAnnualIncome",
              "preference",
              "customRate"
            ]}
          >
            {(values: FormData) => {
              const hasAnswers =
                !!values.filingStatus &&
                !!values.stateOfResidence &&
                !!values.estimatedAnnualIncome;
              const isCalculated = isSavedProfile(values);
              const estimatedTaxRate = isCalculated
                ? queryTaxEstimate.data?.effectiveTaxRate
                : undefined;
              const canUseEstimatedRate = isUsableRate(estimatedTaxRate);
              // `!isFetched` covers the window after the profile write settles
              // and before the newly-enabled estimate query has started.
              const isCalculating =
                saveProfileMeta.isLoading ||
                (isCalculated &&
                  (queryTaxEstimate.isFetching || !queryTaxEstimate.isFetched));

              return (
                <WSList gap="2XL">
                  <WSCard
                    header={{
                      label: "Configure your tax settings",
                      value: {
                        action: {
                          label: "How it works",
                          onClick: () => {
                            modalHowItWorks.open({
                              taxRate: estimatedTaxRate ?? 0
                            });
                          },
                          type: "button"
                        }
                      }
                    }}
                  >
                    <WSList gap="L">
                      <RateSummary
                        rate={estimatedTaxRate}
                        hasAnswers={hasAnswers}
                        isCalculated={isCalculated}
                        isFetching={isCalculating}
                        error={
                          saveProfileMeta.error ??
                          (isCalculated ? queryTaxEstimate.error : undefined)
                        }
                      />

                      {hasAnswers && !canUseEstimatedRate && (
                        <WSActions
                          alignment="left"
                          buttons={[
                            {
                              type: "button",
                              kind: "Secondary",
                              label: "Calculate my rate",
                              loading: isCalculating,
                              onClick: () => {
                                saveProfile({
                                  taxWithholdingSettingsUpdate:
                                    toTaxProfileUpdate(values)
                                });
                              }
                            }
                          ]}
                        />
                      )}

                      <WSForm.Field
                        name="preference"
                        component={WSControlGroup}
                        componentProps={{
                          type: "radio",
                          label: "Set your withholding percentage",
                          required: true,
                          helperText:
                            "You can adjust this anytime as your income changes.",
                          options: [
                            {
                              label: canUseEstimatedRate
                                ? `Use the calculated rate (${selectorTaxRateLabel(estimatedTaxRate)})`
                                : "Use the calculated rate",
                              value: "estimated",
                              disabled: !canUseEstimatedRate,
                              helperText: canUseEstimatedRate
                                ? undefined
                                : "Available once we have calculated your rate."
                            },
                            {
                              label: "Custom rate",
                              value: "custom"
                            }
                          ]
                        }}
                      />

                      {values.preference === "custom" && (
                        <WSForm.Field
                          name="customRate"
                          component={WSInputNumber}
                          componentProps={{
                            min: 0,
                            max: MAX_WITHHOLDING_RATE,
                            maxFractionDigits: 2,
                            align: "right",
                            iconRight: "percent"
                          }}
                        />
                      )}
                    </WSList>
                  </WSCard>

                  <WSText.ParagraphSm color="gray500">
                    Your calculations follow IRS Form 1040-ES methodology.{" "}
                    <a
                      onClick={() => {
                        modalHowWeCalculateTaxRate.open();
                      }}
                    >
                      Learn more
                    </a>{" "}
                    about your rate calculation.
                  </WSText.ParagraphSm>

                  <ErrorAlert context="TaxFilling" error={saveRateMeta.error} />

                  <WSList gap="L">
                    <WSActions
                      alignment="fill"
                      buttons={[
                        {
                          type: "button",
                          kind: "Tertiary",
                          label: "Back",
                          onClick: props.onBack,
                          visible: !!props.onBack
                        },
                        {
                          label: "Continue",
                          loading: saveRateMeta.isLoading,
                          disabled:
                            values.preference !== "custom" &&
                            !canUseEstimatedRate
                        }
                      ]}
                    />

                    {!props.isEdit &&
                      values.preference === "custom" &&
                      values.customRate === 0 && (
                        <WSActions
                          alignment="center"
                          buttons={[
                            {
                              type: "button",
                              kind: "Link",
                              label: "Skip, I’ll handle taxes myself",
                              onClick: props.onSkip
                            }
                          ]}
                        />
                      )}
                  </WSList>
                </WSList>
              );
            }}
          </WSForm.Values>
        </WSList>
      </WSForm>
    );
  };

export const FormTaxRate: React.FC<Props> = props => {
  const querySettings =
    queriesTaxWithholdings.getTaxWithholdingSettings.useQuery();

  // `defaultValues` is read once at mount, so the saved profile has to be
  // resolved before the form exists — otherwise re-entering the screen shows an
  // empty form.
  return (
    <WSQueries queries={{ querySettings }}>
      {({ querySettingsData }) => (
        <TaxRateForm {...props} settings={querySettingsData} />
      )}
    </WSQueries>
  );
};
